/** @jsxImportSource @emotion/react */
import type { MainNavigationActionArgs } from '@/Client/Partials/MainNavigation';
import { MainNavigation } from '@/Client/Partials/MainNavigation';
import { KAI_SHIKI_ALWAYS_DARK_COLORS, KAI_SHIKI_LAYOUT, KAI_SHIKI_TYPOGRAPHY, styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { NewExperienceBanner } from '@/Client/Widgets/NewExperienceBanner';
import { css, SerializedStyles } from '@emotion/react';
import { ReactNode } from 'react';
import { pxToRem } from '../Styles/pxToRem';
import { ADMIN_LEFT_NAV_WIDTH } from './layoutConstants';
import { PageTitleBar } from './PageTitleBar';

const PAGE_LAYOUT_STYLE = css`
  label: PAGE_LAYOUT_STYLE;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--altBackgroundForDarkMode1);
  flex: 1;
  position: fixed;
  left: ${pxToRem(ADMIN_LEFT_NAV_WIDTH)};
  right: 0;
  top: 0;
  bottom: 0;
`;

const PAGE_LAYOUT_CONTENT_STYLE = css`
  label: PAGE_LAYOUT_CONTENT_STYLE;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: ${styleguideColors.sunken};
`;

const STANDARD_CONTENT_STYLE = css`
  label: STANDARD_CONTENT_STYLE;
  padding: ${KAI_SHIKI_LAYOUT.spacing2}px;
  min-height: '100vh';
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
`;

const SUB_HEADER_STYLE = css`
  label: SUB_HEADER_STYLE;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SUB_TITLE_STYLE = css`
  label: SUB_TITLE_STYLE;
  background: ${KAI_SHIKI_ALWAYS_DARK_COLORS.background.dark};
  height: ${KAI_SHIKI_LAYOUT.spacing6}px;
  display: flex;
  align-items: center;
  color: ${KAI_SHIKI_ALWAYS_DARK_COLORS.text.white};
  padding-left: ${KAI_SHIKI_LAYOUT.spacing2}px;
  padding-right: ${KAI_SHIKI_LAYOUT.spacing2}px;
  ${KAI_SHIKI_TYPOGRAPHY.xLargeBold}
  text-transform: uppercase;
`;

const SUB_TITLE_BUTTONS_STYLE = css`
  label: SUB_TITLE_BUTTONS_STYLE;
  display: flex;
  margin-left: auto;
`;

const SUB_HEADER_BUTTONS_STYLE = css`
  label: SUB_HEADER_BUTTONS_STYLE;
  display: flex;
  margin-left: auto;
`;

const CENTERED_CONTENT_STYLE = css`
  label: CENTERED_CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export type ContentLayoutStyling = 'none' | 'standard' | 'centered';

const CONTENT_STYLES: Record<ContentLayoutStyling, SerializedStyles> = {
  none: css``,
  standard: STANDARD_CONTENT_STYLE,
  centered: CENTERED_CONTENT_STYLE,
};

export const PageLayout = ({
  title,
  titleIcons,
  headerButtons,
  header,
  mainNavigationArgs,
  orgPrefix,
  subTitle,
  subTitleButtons,
  subheader,
  subHeaderButtons,
  children,
  onBack,
  contentLayoutStyling = 'none',
  hidden = false,
  forceProjectLifecycleChrome = false,
}: {
  title: string;
  titleIcons?: ReactNode[];
  /** Rendered by PageTitleBar when forceProjectLifecycleChrome is false. */
  headerButtons?: ReactNode;
  /** MainNavigation child slot for lifecycle navigation such as StageStepper */
  header?: ReactNode;
  /** Drives config-decided top-bar actions on project lifecycle routes. */
  mainNavigationArgs?: MainNavigationActionArgs;
  /** Rendered by PageTitleBar when forceProjectLifecycleChrome is false. */
  orgPrefix?: ReactNode;
  subTitle?: ReactNode;
  subTitleButtons?: ReactNode;
  subheader?: ReactNode;
  subHeaderButtons?: ReactNode;
  children: ReactNode;
  onBack?: () => void;
  contentLayoutStyling?: 'none' | 'standard' | 'centered';
  hidden?: boolean;
  /**
   * Forces the project lifecycle chrome shell (a full-width MainNavigation
   * plus scrollable content, no left-nav offset). The only current caller is
   * AdminScanPageNew, across its whole "Scan Flow" (edit, review, approved,
   * closeout). Other PageLayout callers, including the legacy AdminScanPage
   * routes (/scope, /change-order, /punchlist), don't pass this and are
   * unaffected.
   */
  forceProjectLifecycleChrome?: boolean;
}) => {
  // ── Universal chrome entry point ──────────────────────────────
  // Project lifecycle routes (closeout, scope, etc.) render a full-width
  // MainNavigation shell with no left-nav offset. The page renders StageStepper
  // into its header slot using useProjectStateLifecycle to drive stage state.
  // All other routes render the standard admin/kaiPro layout below.
  if (forceProjectLifecycleChrome) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          ...(hidden ? { display: 'none' } : undefined),
        }}
      >
        <MainNavigation
          args={mainNavigationArgs}
          header={header}
          title={title}
        />
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {(subheader || subHeaderButtons) && (
            <div css={SUB_HEADER_STYLE}>
              {subheader}
              {subHeaderButtons && <div css={SUB_HEADER_BUTTONS_STYLE}>{subHeaderButtons}</div>}
            </div>
          )}
          {children}
        </div>
        <NewExperienceBanner />
      </div>
    );
  }

  return (
    <div
      css={PAGE_LAYOUT_STYLE}
      style={{
        left: pxToRem(ADMIN_LEFT_NAV_WIDTH),
        ...(hidden ? { display: 'none' } : undefined),
      }}
    >
      <PageTitleBar
        title={title}
        titleIcons={titleIcons}
        headerButtons={headerButtons}
        orgPrefix={orgPrefix}
        onBack={onBack}
      />
      {subTitle && (
        <div css={SUB_TITLE_STYLE}>
          {subTitle}
          {subTitleButtons && <div css={SUB_TITLE_BUTTONS_STYLE}>{subTitleButtons}</div>}
        </div>
      )}
      <div
        css={[PAGE_LAYOUT_CONTENT_STYLE, CONTENT_STYLES[contentLayoutStyling]]}
      >
        {(subheader || subHeaderButtons) && (
          <div css={SUB_HEADER_STYLE}>
            {subheader}
            {subHeaderButtons && <div css={SUB_HEADER_BUTTONS_STYLE}>{subHeaderButtons}</div>}
          </div>
        )}
        {children}
      </div>
      <NewExperienceBanner />
    </div>
  );
};
